import type {
  AdminContactItem,
  AdminStats,
  CreateOperatorRequest,
  CreateOperatorResponse,
  OperatorSummary,
  UpdateOperatorStatusRequest,
} from "@vb/rgs-contract";

export interface AdminSession {
  sessionId: string;
  operatorId: string;
  playerId: string;
  currency: string;
  /** Which game this session is bound to. Drives the game column on the
   *  Sessions tab and the GameFilter pill's narrowing. */
  gameSlug: "vault-breaker" | "pressure-plate" | "cipher-crack";
  balance: number;
  nonce: number;
  activeRoundId: string | null;
  serverSeedHash: string;
  socketCount: number;
}

const BASE = (import.meta as unknown as { env: { VITE_RGS_BASE?: string } }).env.VITE_RGS_BASE ?? "";
const TOKEN_KEY = "vb-admin-token";

export function saveToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}
export function loadToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = loadToken();
  if (!token) throw new Error("Not logged in");
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    clearToken();
    throw new Error("Invalid or expired admin token");
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export const admin = {
  verify: () => call<{ ok: true }>("/admin/verify"),
  getStats: () => call<AdminStats>("/admin/stats"),
  listSessions: () => call<AdminSession[]>("/admin/sessions"),
  listOperators: () => call<OperatorSummary[]>("/admin/operators"),
  createOperator: (body: CreateOperatorRequest) =>
    call<CreateOperatorResponse>("/admin/operators", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  setStatus: (id: string, body: UpdateOperatorStatusRequest) =>
    call<OperatorSummary>(`/admin/operators/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  rotateKey: (id: string) =>
    call<{ apiKey: string }>(
      `/admin/operators/${encodeURIComponent(id)}/rotate-key`,
      { method: "POST" },
    ),
  deleteOperator: (id: string) =>
    call<{ ok: true }>(`/admin/operators/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setTransparency: (id: string, optin: boolean) =>
    call<{ ok: true; optin: boolean }>(
      `/admin/operators/${encodeURIComponent(id)}/transparency`,
      { method: "PATCH", body: JSON.stringify({ optin }) },
    ),
  listContacts: () => call<AdminContactItem[]>("/admin/contacts"),
  // Onboarding kit re-pull. Returns embed URL + REST endpoints. Does NOT
  // include the API key — for a fresh key use rotateKey().
  getKit: (id: string) =>
    call<OperatorKit>(`/admin/operators/${encodeURIComponent(id)}/kit`),
  // Settlements (studio's 10% cut, paid weekly off-chain in USDT/USDC).
  listSettlements: (operatorId?: string) =>
    call<{ settlements: Settlement[] }>(
      operatorId
        ? `/admin/settlements?operatorId=${encodeURIComponent(operatorId)}`
        : "/admin/settlements",
    ),
  recordSettlement: (id: string, body: RecordSettlementRequest) =>
    call<{ ok: true; settlement: Settlement; totalPaidBtc: number }>(
      `/admin/operators/${encodeURIComponent(id)}/settlements`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  // In-app notifications (replaces the old Slack/email pings).
  // The list endpoint returns the unreadCount alongside the items so the
  // panel's badge updates atomically with the list — no second round-trip.
  listNotifications: (opts: { limit?: number; unreadOnly?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set("limit", String(opts.limit));
    if (opts.unreadOnly) qs.set("unread", "true");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return call<{ items: AdminNotification[]; unreadCount: number }>(
      `/admin/notifications${suffix}`,
    );
  },
  markNotificationsRead: (ids: number[]) =>
    call<{ markedCount: number }>(`/admin/notifications/read`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  markAllNotificationsRead: () =>
    call<{ markedCount: number }>(`/admin/notifications/read-all`, {
      method: "POST",
    }),
};

export interface AdminNotification {
  id: number;
  kind: string;
  payload: Record<string, unknown>;
  readAt: number | null;
  createdAt: number;
}

export interface OperatorKit {
  operatorId: string;
  operatorName: string;
  status: string;
  billingEmail: string | null;
  websiteUrl: string | null;
  contactName: string | null;
  embedUrl: string;
  apiBaseUrl: string;
  endpoints: {
    sessionLaunch: string;
    roundOpen: string;
    roundResolve: string;
    roundRollback: string;
    balance: string;
    verify: string;
  };
  webhookEvents: string[];
}

export interface Settlement {
  id: number;
  operator_id: string;
  /** Legacy BTC-only column — preserved for back-compat. New rows mirror
   *  `amount` here only when `currency === "BTC"`; non-BTC rows have 0. */
  amount_btc: number;
  /** Currency-agnostic settled amount. Always present after the V2 migration. */
  amount: number;
  /** ISO-style currency code (BTC, ETH, SOL, USDT, USD). */
  currency: string;
  /** Optional per-game scoping. Null = lump settlement across all games. */
  game_slug: string | null;
  tx_hash: string | null;
  note: string | null;
  paid_at: number;
  created_at: number;
}

export interface RecordSettlementRequest {
  /** Canonical amount in `currency` units. */
  amount?: number;
  currency?: string;
  /** Optional per-game settlement (e.g. operator paid us only the Pressure
   *  Plate cut this period). Null/undefined = lump across all games. */
  gameSlug?: string;
  /** Legacy field — server normalizes to {amount: amountBtc, currency: BTC}. */
  amountBtc?: number;
  txHash?: string;
  note?: string;
  paidAt?: number;
}
